<template>
<!-- eslint-disable max-len -->
<edit-page
  name="Minor Decision"
  model-name="individualDecision"
  param="individualDecisionId"
  page-prefix="/blade/minor-decisions"
  :create="createIndividualDecision"
  :fetch="getIndividualDecision"
  :fetch-types="getIndividualDecisionTypes"
  :update="updateIndividualDecision"
  :remove="deleteIndividualDecision"
  :enum-names="['characters', 'variants', 'metrics', 'narrativeTypes', 'speakers', 'choiceDisplayTypes']"
  :stub="individualDecision"
  :validator="validator"
  :utils="utils">
<template slot-scope="{ model: individualDecision, enums: { characters, variants, metrics, narrativeTypes, speakers, choiceDisplayTypes }, utils: { unused }, validator }">
  <text-input
    label
    v-model.trim="individualDecision.name"
    :validator="validator.name"
    required
    autofocus
  >Name</text-input>

  <text-input
    label
    v-model.trim="individualDecision.title"
    :validator="validator.title"
    required
  >Title</text-input>

  <collapsible-section
    :name="`Decision Settings for: ${individualDecision.title || '[missing title]'}`"
    id="d-set"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <checkbox-input v-model="individualDecision.hideTitle">
      No Title in Decision Display (only relevant to non-bubble decisions)
    </checkbox-input>

    <text-input
      v-if="!(individualDecision.hideTitle)"
      label
      v-model.trim="individualDecision.displayTitle"
    >Display Title (falls back to Title; only relevant to non-bubble decisions)</text-input>

    <text-input
      label
      v-model.trim="individualDecision.reportTitle"
    >Report Title (falls back to Title)</text-input>

    <text-input label v-model.trim="individualDecision.description" textarea>Description</text-input>

    <text-input label v-model.trim="individualDecision.submitButtonText" placeholder="default: Reply (bubble) or Approve (non-bubble)">
      Submit Button Text (optional)
    </text-input>

    <text-input label v-model.trim="individualDecision.backButtonText" placeholder="default: Back">
      Back Button Text (optional)
    </text-input>

    <text-input label v-model.trim="individualDecision.prompt">Prompt (optional)</text-input>

    <checkbox-input v-model="individualDecision.feedback">Feedback</checkbox-input>

    <checkbox-input v-model="individualDecision.noWaitForIFrames">Don't Force Wait For IFrames To Load</checkbox-input>

    <checkbox-input v-model="individualDecision.forceConfirmation">Prevent Automatically Skipping Choice Confirmation</checkbox-input>

    <checkbox-input v-model="individualDecision.forceSkipConfirmation">Force Automatically Skipping Choice Confirmation</checkbox-input>

    <checkbox-input v-model="individualDecision.disableReasoningForm">Disable Reasoning Form</checkbox-input>

    <checkbox-input v-model="individualDecision.preventReasoningInputOverride">Prevent Reasoning Input Override (from Class)</checkbox-input>

    <number-input
      label
      min-value="0"
      max-value="250"
      v-model="individualDecision.reasoningMinimumLength"
      :validator="validator.reasoningMinimumLength"
    >
      Reasoning Minimum Length in Characters
    </number-input>

    <checkbox-input v-model="individualDecision.reasoningMinimumLengthOverride">Reasoning Minimum Length Override (use this over Gamulation/Class/Workgroup settings)</checkbox-input>

    <checkbox-input v-model="individualDecision.displayOneLineChoices">Display One Line Choices</checkbox-input>

    <checkbox-input v-model="individualDecision.dynamicHeightChoices">Dynamic Height Choices (note: disables wrong choice bubble position offset)</checkbox-input>

    <select-input
      label
      v-model="individualDecision.choiceDisplayType"
      :options="choiceDisplayTypes"
    >Choice Display Type</select-input>

    <checkbox-input v-model="individualDecision.preventChoiceDisplayTypeOverride">Prevent Choice Display Type Override (from Class)</checkbox-input>

    <text-input
      label
      v-model.trim="individualDecision.choiceSelectButtonText"
      placeholder="default: Reply (bubble) or Approve (non-bubble)"
    >Choice Select Button Text (optional) (applies to carousel display type)</text-input>

    <img v-if="individualDecision.image" class="form-image-character" :src="resolveAssetPath(individualDecision.image, 'images')">
    <asset-select-input @input="onImageChange(individualDecision)" label v-model="individualDecision.image" legacy-manifest-path="images">Decision Image</asset-select-input>

    <video style="max-width: 800px;" v-if="individualDecision.video" :src="resolveAssetPath(individualDecision.video, 'videos')" class="video" controls playsinline/>
    <asset-select-input @input="onVideoChange(individualDecision)" label v-model="individualDecision.video" legacy-manifest-path="videos">Decision Video</asset-select-input>
  </collapsible-section>

  <collapsible-section
    :name="`Decision Choices for: ${individualDecision.title || '[missing title]'}`"
    id="d-choice"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <array-input :items="individualDecision.choices" :stub="{ metricAdjustments: [] }" :validator="validator.choices" :disabled="validator.choices.$invalid && individualDecision.choices.length > 0" required border>
      <h4 slot="title">Choices</h4>

      <template slot="item" slot-scope="{ item: choice, index: i }">
        <text-input label v-model.trim="choice.title" :validator="validator.choices.$each[i].title" required autofocus :remove="() => { remove(individualDecision.choices, i); }" :up="() => { up(individualDecision.choices, i); }" :down="() => { down(individualDecision.choices, i); }">Title</text-input>

        <collapsible-section
          :name="`Choice Settings for: ${choice.title || '[missing title]'}`"
          :id="`ch-set${i}`"
          bold
          indent
          :buttonStyle="{
            'font-size': '110%'
          }"
        >
          <text-input label v-model.trim="choice.description" textarea>Description</text-input>

          <img v-if="choice.image" class="form-image-background" :src="resolveAssetPath(choice.image, 'images')">
          <asset-select-input label v-model="choice.image" legacy-manifest-path="images">Choice Image</asset-select-input>

          <select-input
            label
            v-model="choice.playerVariantImage"
            :options="variants"
          >
            Player Variant Image (falls back to Image if variant not found)
          </select-input>

          <checkbox-input v-if="choice.image" v-model="choice.showTitle">
            Show Title Under Image
          </checkbox-input>

          <checkbox-input @input="(checked) => { if (checked && choice.next) { choice.next = null; } }" v-model="choice.wrong">
            Wrong Choice (remove and loopback when selected)
          </checkbox-input>

          <checkbox-input v-model="choice.removeIfPreviouslyChosen">
            Remove If Previously Chosen (prevent from being presented again in a Playthrough once chosen)
          </checkbox-input>

          <array-input class="offset-sm-1" :items="choice.metricAdjustments" :disabled="validator.choices.$each[i].metricAdjustments.$invalid" :hide-button="choice.metricAdjustments.length >= metrics.filter(m => m.type === 'individual').length" border>
            <h5 slot="title">Metric Adjustments</h5>

            <div class="form-row" slot="item" slot-scope="{ item: metricAdjustment, index: j }">
              <select-input class="col-sm-6" v-model="metricAdjustment.metric" :options="unused(metrics.filter(m => m.type === 'individual')).by('_id').in(choice.metricAdjustments).by('metric').at(j)" use="_id" :display="m => { return `${m.name} (${m.type})`; }" autofocus :to="(id) => { return `/blade/metrics/${id}/edit`; }">Metric</select-input>

              <number-input class="col-sm-4" v-model="metricAdjustment.adjustment" :validator="validator.choices.$each[i].metricAdjustments.$each[j].adjustment" required :remove="() => { choice.metricAdjustments.splice(j, 1); }">Adjustment</number-input>

              <checkbox-input class="col-sm-1" v-model="metricAdjustment.setToValue">
                Set
              </checkbox-input>
            </div>
            <span slot="button">Add Metric Adjustment</span>
          </array-input>
        </collapsible-section>

        <collapsible-section
          :name="`Choice Narrative for: ${choice.title || '[missing title]'}`"
          :id="`ch-nar${i}`"
          bold
          indent
          :buttonStyle="{
            'font-size': '110%'
          }"
        >
          <narrative-input
            :narratives="choice.narratives"
            :validator="validator.choices.$each[i].narratives"
            :narrative-types="narrativeTypes"
            :speakers="speakers"
            :characters="characters"
            :variants="variants"
            required
            no-title
          >Choice Narrative</narrative-input>
        </collapsible-section>

        <div style="background-color: grey; height: 2px; width: 100%;"></div>
      </template>
      <span slot="button">Add Choice</span>
    </array-input>
  </collapsible-section>

  <collapsible-section
    :name="`Decision Narrative for: ${individualDecision.title || '[missing title]'}`"
    id="d-nar"
    bold
    :buttonStyle="{
      'font-size': '140%'
    }"
  >
    <narrative-input
      :narratives="individualDecision.narratives"
      :validator="validator.narratives"
      :narrative-types="narrativeTypes"
      :speakers="speakers"
      :characters="characters"
      :variants="variants"
      required
      no-title
      :sections="narrativeSections(individualDecision.narratives, narrativeTypes)"
    >Narrative</narrative-input>
  </collapsible-section>
</template>
</edit-page>
</template>

<script>
import { mapActions } from 'vuex';
import { required, maxValue, minValue } from 'vuelidate/lib/validators';

import { narrative } from '../../../utils/validators';
import { unused } from '../../../utils/utils';

import EditPage from '../../ui/pages/EditPage';
import NarrativeInput from '../../ui/inputs/NarrativeInput';

import ArrayInput from '../../ui/inputs/ArrayInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import NumberInput from '../../ui/inputs/NumberInput';
import AssetSelectInput from '../../ui/inputs/AssetSelectInput';
import SelectInput from '../../ui/inputs/SelectInput';
import TextInput from '../../ui/inputs/TextInput';
import CollapsibleSection from '../../ui/inputs/CollapsibleSection';

export default {
  components: {
    EditPage,
    NarrativeInput,
    ArrayInput,
    CheckboxInput,
    NumberInput,
    AssetSelectInput,
    SelectInput,
    TextInput,
    CollapsibleSection,
  },
  data() {
    return {
      utils: {
        unused
      },
      individualDecision: {
        _id: '',
        name: '',
        title: '',
        hideTitle: false,
        displayTitle: '',
        reportTitle: '',
        description: '',
        submitButtonText: '',
        backButtonText: '',
        prompt: '',
        image: '',
        feedback: false,
        forceConfirmation: false,
        forceSkipConfirmation: false,
        disableReasoningForm: false,
        preventReasoningInputOverride: false,
        reasoningMinimumLength: 0,
        reasoningMinimumLengthOverride: false,
        displayOneLineChoices: false,
        dynamicHeightChoices: false,
        choiceDisplayType: 'list',
        preventChoiceDisplayTypeOverride: false,
        narratives: [],
        choices: []
      },
      validator: {
        name: { required },
        title: { required },
        reasoningMinimumLength: { maxValue: maxValue(250), minValue: minValue(0) },
        choices: {
          required,
          $each: {
            title: { required },
            metricAdjustments: {
              $each: {
                metric: { required },
                adjustment: { required }
              }
            },
            narratives: narrative()
          }
        },
        narratives: narrative({ required: true })
      }
    };
  },
  methods: {
    ...mapActions([
      'createIndividualDecision',
      'getIndividualDecision',
      'getIndividualDecisionTypes',
      'updateIndividualDecision',
      'deleteIndividualDecision'
    ]),
    onImageChange(individualDecision) {
      this.$set(individualDecision, 'video', null);
    },
    onVideoChange(individualDecision) {
      this.$set(individualDecision, 'image', null);
    },
    remove(items, index) {
      items.splice(index, 1);
    },
    up(items, index) {
      if (index <= 0) return;
      const [item] = items.splice(index, 1);
      items.splice(index - 1, 0, item);
    },
    down(items, index) {
      if (index >= items.length - 1) return;
      const [item] = items.splice(index, 1);
      items.splice(index + 1, 0, item);
    },
    narrativeSections(narratives, narrativeTypes) {
      let di = narratives.findIndex(n => n.type && n.type.includes('Decision'));
      if (di < 0) {
        narratives.push({
          images: [],
          type: 'leftDecisionBubble',
          playerBackground: true,
          speaker: 'left',
          text: 'Make a decision...'
        });
        di = 0;
      }
      return [
        {
          name: 'Intro Narrative',
          start: (di === 0) ? -1 : 0,
          end: di,
          types: narrativeTypes.filter(t => !t.includes('Decision')),
        },
        {
          name: 'Decision',
          start: di,
          end: di + 1,
          noButton: true,
          noRemove: true,
          noReorder: true,
          types: narrativeTypes.filter(t => t.includes('Decision')),
        },
        {
          name: 'Outro Narrative',
          start: di + 1,
          end: narratives.length,
          types: narrativeTypes.filter(t => !t.includes('Decision')),
        },
      ];
    },
  },
};
</script>

<style lang="scss" scoped>
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/IndividualDecisionEdit.vue